import PrestaGlobals from '../../common/PrestaGlobals';

export type Intent = 'order' | 'modify' | 'question';
export type Slot = 'morning' | 'afternoon' | 'evening' | 'any';
export type Timeline = '1m' | '1-3m' | 'later';
export type Measures = 'yes' | 'rough' | 'survey';
export type InterestStep = 1 | 2 | 3;

export interface InterestDraft {
    step: InterestStep;
    intent: Intent | null;
    name: string;
    phone: string;
    town: string;
    slot: Slot;
    message: string;
    consent: boolean;
    sent: boolean;          // step 2 submitted
    sentPhone: string;      // formatted by server
    extraSent: boolean;     // step 3 submitted
}

export const PHONE_PREFIX = '+36 ';

export const emptyDraft = (): InterestDraft => ({
    step: 1, intent: null, name: '', phone: PHONE_PREFIX, town: '', slot: 'any',
    message: '', consent: false, sent: false, sentPhone: '', extraSent: false,
});

const storageKey = (quoteId: number): string => `uc_interest:${quoteId}`;

export const loadDraft = (quoteId: number): InterestDraft => {
    try {
        const raw = localStorage.getItem(storageKey(quoteId));
        return raw ? { ...emptyDraft(), ...JSON.parse(raw) } : emptyDraft();
    } catch {
        return emptyDraft();
    }
};

export const saveDraft = (quoteId: number, draft: InterestDraft): void => {
    try {
        localStorage.setItem(storageKey(quoteId), JSON.stringify(draft));
    } catch {
        /* storage unavailable — draft lives in memory only */
    }
};

// Mirrors UCC::leadPhone
export const phoneValid = (raw: string): boolean => {
    let d = raw.replace(/\D/g, '');
    if (d.length >= 10 && (d.startsWith('36') || d.startsWith('06'))) d = d.slice(2);
    return /^[1-9]\d{7,8}$/.test(d);
};

// Hungarian order: given name last
export const firstName = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    return parts.length > 1 ? parts.slice(1).join(' ') : parts[0] ?? '';
};

const SUBMIT_ERRORS: Record<string, string> = {
    ratelimit: 'Az előbb már elküldted. Próbáld újra egy perc múlva.',
    invalid: 'Kérjük, ellenőrizd a megadott adatokat.',
    photo: 'A fotó nem tölthető fel (JPEG, PNG vagy WEBP, max. 8 MB).',
    toomanyphotos: 'Legfeljebb 3 fotót tölthetsz fel.',
    alreadysent: 'Ezt már elküldted, köszönjük!',
    expired: 'Ez az árajánlat link lejárt.',
};
const SUBMIT_FAILED = 'A küldés nem sikerült. Kérjük, próbáld újra, vagy hívj minket.';

async function post(method: string, body: FormData): Promise<any> {
    const url = PrestaGlobals.cartUrl(method) + '&token=' + encodeURIComponent(PrestaGlobals.quoteToken);
    let data: any;
    try {
        data = await (await fetch(url, { method: 'POST', body })).json();
    } catch {
        throw new Error(SUBMIT_FAILED);
    }
    if (!data?.ok) throw new Error(SUBMIT_ERRORS[data?.error] ?? SUBMIT_FAILED);
    return data;
}

/** Step 2: creates the lead (CRM + e-mail); returns the formatted phone */
export async function submitInterest(draft: InterestDraft): Promise<string> {
    const params = new URLSearchParams(window.location.search);
    const body = new FormData();
    body.append('intent', draft.intent ?? '');
    body.append('name', draft.name.trim());
    body.append('phone', draft.phone);
    body.append('town', draft.town.trim());
    body.append('slot', draft.slot);
    if (draft.intent !== 'order') body.append('message', draft.message.trim());
    body.append('consent', draft.consent ? '1' : '0');
    body.append('utm_source', params.get('utm_source') ?? '');
    body.append('utm_campaign', params.get('utm_campaign') ?? '');
    return (await post('quoteinterest', body)).phone;
}

/** Step 3: optional answers + photos onto the same lead */
export async function submitInterestExtra(timeline: Timeline | null, measures: Measures | null, photos: { blob: Blob; name: string }[]): Promise<void> {
    const body = new FormData();
    body.append('timeline', timeline ?? '');
    body.append('measures', measures ?? '');
    photos.forEach((p) => body.append('photos[]', p.blob, p.name));
    await post('quoteinterestextra', body);
}

/** Phone photo -> JPEG, longest side <= max; original on failure */
export async function downscalePhoto(file: File, max = 2000): Promise<Blob> {
    try {
        const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();
        return await new Promise<Blob>((resolve) => canvas.toBlob((b) => resolve(b ?? file), 'image/jpeg', 0.85));
    } catch {
        return file;
    }
}
